import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ConversationList, ChatWindow } from '../../components/messaging';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../components/ui/resizable';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import chatIcon from '../../assets/img3d/chat.png';

const ClientMessagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    conversations,
    currentConversation,
    messages,
    isLoading,
    isLoadingMessages,
    loadConversations,
    loadConversation,
    clearConversation,
    sendMessage,
    markAllAsRead,
  } = useMessaging();

  const [isMobileViewingChat, setIsMobileViewingChat] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const sidebarPanelRef = useRef(null);
  const hasMarkedAsReadRef = useRef(false);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Mark all conversations as read when opening the messages page (only once)
  useEffect(() => {
    if (!isLoading && conversations.length > 0 && !hasMarkedAsReadRef.current) {
      hasMarkedAsReadRef.current = true;
      markAllAsRead();
    }
  }, [isLoading, conversations.length, markAllAsRead]);

  const toggleSidebar = () => {
    if (sidebarPanelRef.current) {
      if (sidebarPanelRef.current.isCollapsed()) {
        sidebarPanelRef.current.expand();
      } else {
        sidebarPanelRef.current.collapse();
      }
    }
  };

  const handleSelectConversation = async (conversation) => {
    await loadConversation(conversation.id);
    setIsMobileViewingChat(true);
  };

  const handleSendMessage = async (content) => {
    await sendMessage(content);
  };

  const handleBack = () => {
    clearConversation();
    setIsMobileViewingChat(false);
  };

  return (
    <Layout
      headerConfig={{
        icon: chatIcon,
        iconColor: 'bg-white',
        title: 'Mes Messages',
        subtitle: 'Discutez avec les agents et professionnels',
        primaryAction: 'Mon Espace',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/client'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      <Card className="h-[calc(100vh-280px)] min-h-[500px]">
        <CardContent className="p-0 h-full">
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Conversation List - Resizable and Collapsible */}
            <ResizablePanel
              ref={sidebarPanelRef}
              defaultSize={25}
              minSize={15}
              maxSize={40}
              collapsible={true}
              collapsedSize={0}
              onCollapse={() => setIsSidebarCollapsed(true)}
              onExpand={() => setIsSidebarCollapsed(false)}
              className={`
                ${isMobileViewingChat ? 'hidden md:block' : 'block'}
              `}
            >
              <div className="h-full flex flex-col border-r border-gray-200">
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">Conversations</h2>
                    <p className="text-xs text-gray-500">{conversations.length} conversation(s)</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <ConversationList
                    conversations={conversations}
                    selectedId={currentConversation?.id}
                    onSelect={handleSelectConversation}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle className={isMobileViewingChat ? 'hidden md:flex' : 'flex'} />

            {/* Chat Window */}
            <ResizablePanel
              defaultSize={75}
              minSize={60}
              className={`
                flex flex-col relative
                ${!isMobileViewingChat ? 'hidden md:flex' : 'flex'}
              `}
            >
              {isSidebarCollapsed && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="h-8 w-8 rounded-r-lg border border-l-0 border-gray-200 bg-white shadow-sm"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <ChatWindow
                conversation={currentConversation}
                messages={messages}
                currentUserId={user?.id || ''}
                onSendMessage={handleSendMessage}
                onBack={handleBack}
                isLoading={isLoading}
                isLoadingMessages={isLoadingMessages}
                isMobile={false}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ClientMessagesPage;

